import { useCallback, useState } from 'react'
import { Dispatch, SetStateAction } from 'react'
import { Form } from 'react-final-form'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'
import { useToasts } from 'react-toast-notifications'

import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird'

import { INFURA_WEBSITE_URL } from '@infura/packages.common/constants'
import Notification from '@infura/packages.components/Notification'
import { Button } from '@infura/packages.ui/Button'
import { Card } from '@infura/packages.ui/Card'

import FieldInput from 'components/FieldInput'

import { api } from 'lib/api/api'
import handleSubmissionError from 'lib/handleSubmissionError'
import { Infura } from 'lib/types/infuraTypes'

interface RecoveryFormValues {
  email: string
}

interface RecoverMFAProps {
  setLoginStep: Dispatch<SetStateAction<Infura.AUTH_LOGIN_STEP>>
}

const FormReset = ({
  onSubmit,
  setLoginStep
}: Readonly<{
  onSubmit?: () => void
  setLoginStep: Dispatch<SetStateAction<Infura.AUTH_LOGIN_STEP>>
}>) => {
  const { addToast } = useToasts()

  const handleSubmit = useCallback(
    async ({ email }: RecoveryFormValues) => {
      try {
        await api.recoverMFA({ email })
        addToast('Ticket created successfully', {
          appearance: 'success',
          autoDismiss: true
        })
        if (onSubmit) onSubmit()
      } catch (e: any) {
        return handleSubmissionError(e)
      }
    },
    [addToast, onSubmit]
  )

  return (
    <Card className='w-full bg-transparent pb-6 shadow-none'>
      <h1 className='font-body mb-10 text-4xl font-bold'>Recover Your Account</h1>
      <p className='mb-8 text-lg'>
        Verify the email associated with your account. A support ticket will be raised associated with this account.
      </p>
      <Form
        onSubmit={handleSubmit}
        render={({ handleSubmit, submitError, submitting }) => (
          <>
            {submitError ? (
              <Notification className='mb-6' color='alert' data-testid='auth-form-error' size='sm' variant='outlined'>
                <span className='w-full text-center'>{submitError}</span>
              </Notification>
            ) : null}
            <form className='text-lg' data-testid='auth-form' onSubmit={handleSubmit}>
              <div>
                <FieldInput
                  className='mb-7 !h-auto !px-6 !py-5 text-lg 2xl:text-xl'
                  data-testid='recovery_email'
                  id='email'
                  label={<span className='mb-2'>Enter your account email</span>}
                  name='email'
                  placeholder='Type your email'
                  required
                  type='email'
                />

                <div className='flex flex-col items-center gap-6 2xl:flex-row'>
                  <Button
                    className='2xl:mb-10 2xl:!w-auto'
                    data-testid='submit-button'
                    disabled={submitting}
                    fullWidth
                    type='submit'
                  >
                    {submitting ? <FontAwesomeIcon className='fa-fw fa-spin' icon={faSpinnerThird} /> : 'Submit'}
                  </Button>
                  <Button
                    className='text-lg 2xl:mb-10 2xl:!w-auto'
                    data-testid='auth-button'
                    disabled={submitting}
                    fullWidth
                    onClick={() => setLoginStep(Infura.AUTH_LOGIN_STEP.MFA_VERIFY)}
                    theme='secondary'
                    type='button'
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          </>
        )}
      />
    </Card>
  )
}

const Confirmation = () => {
  return (
    <Card className='w-full bg-transparent pb-6 shadow-none'>
      <h1 className='font-body mb-10 text-4xl font-bold'>Recover Your Account</h1>
      <p className='mb-8 text-lg'>
        Your account recovery request has been submitted. We will follow up with you within 1-3 business days.
      </p>
      <div className='flex flex-col items-center gap-6 2xl:flex-row'>
        <Button
          className='2xl:!w-auto'
          data-testid='return-home-button'
          fullWidth
          href={INFURA_WEBSITE_URL}
          type='button'
        >
          Return to Dashboard
        </Button>
      </div>
    </Card>
  )
}

export default function RecoverMFA({ setLoginStep }: RecoverMFAProps) {
  const [step, setStep] = useState(0)

  return (
    <section className='flex h-full w-full items-center justify-center'>
      <div className='w-full max-w-[37rem] px-6'>
        {step === 0 ? <FormReset onSubmit={() => setStep(1)} setLoginStep={setLoginStep} /> : null}
        {step === 1 ? <Confirmation /> : null}
      </div>
    </section>
  )
}
